import clsx from 'clsx'
import { useFormik } from 'formik'
import React from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { useMutation } from '@apollo/client'
import { CREATE_FORM_BUILDER } from '../../../../graphql/mutation'
import { GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID } from '../../../../graphql/query'
import { useNavigate } from 'react-router-dom'
import './CreateFormModal.scss'

interface IProps {
  show: boolean
  closeModal: () => void
  setIsModalOpen: (open: boolean) => void
  formJson: any
  templateId: any
}

const CreateBotSchema = Yup.object().shape({
  formName: Yup.string()
    .trim()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .matches(
      /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/,
      'Name must contain at least one non-white space character'
    )
    .required('Name is required'),
  formDescription: Yup.string()
    .trim()
    .min(2, 'Minimum 2 characters')
    .max(100, 'Maximum 100 characters')
    .matches(
      /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/,
      'Description must contain at least one non-white space character'
    )
    .required('Description is required'),
  expiryDate: Yup.string()
})

const initialValues = {
  formName: '',
  formDescription: '',
  expiryDate: '',
  formType: 'normal' as 'normal' | 'prefilled' | 'conditional'
}

const FORM_TYPES = [
  {
    value: 'normal' as const,
    label: 'Basic Form',
    description: 'One public link shared with everyone via email, QR, or social.',
    icon: 'bi-link-45deg',
    iconClass: 'create-form-modal__type-icon--normal'
  },
  {
    value: 'prefilled' as const,
    label: 'Prefilled Form',
    description: 'Upload Excel on share to create a personalized form for each recipient.',
    icon: 'bi-person-lines-fill',
    iconClass: 'create-form-modal__type-icon--prefilled'
  },
  {
    value: 'conditional' as const,
    label: 'Conditional Form',
    description:
      'Same Excel prefill flow, but recipient edits are final on submit — no approve/reject review.',
    icon: 'bi-person-check',
    iconClass: 'create-form-modal__type-icon--conditional'
  }
]

const CreateFormModal: React.FC<IProps> = ({
  show,
  closeModal,
  setIsModalOpen,
  formJson,
  templateId
}) => {
  const navigate = useNavigate()
  const [createFormBuilder] = useMutation(CREATE_FORM_BUILDER, {
    refetchQueries: [
      {
        query: GET_ALL_FORM_BUILDER_BY_BG_ACCOUNT_ID,
        variables: {
          input: {
            formUuid: null
          }
        },
        context: {
          headers: {
            'access-control-allow-methods': 'Form-Builder'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: CreateBotSchema,
    onSubmit: (values, { resetForm }) => {
      createFormBuilder({
        variables: {
          input: {
            templateID: templateId,
            name: values.formName,
            formData: formJson,
            description: values.formDescription,
            expiryDate: values.expiryDate === '' ? null : values.expiryDate,
            cardBackground: '',
            cardStyle: '',
            isPrefillForm: values.formType !== 'normal',
            prefillRequiresApproval: values.formType === 'prefilled'
          }
        }
      })
        .then((res: any) => {
          if (res) {
            const formUuid = res.data?.createFormBuilder?.formUuid
            toast.success(res.data.createFormBuilder.message)
            resetForm()
            closeModal()
            if (formUuid) {
              navigate(`/automation/edit-form/${formUuid}`)
            } else {
              navigate('/automation/process-automation/form-builder')
            }
          } else {
            toast.error('Form name already exists.')
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  })

  const handleClose = () => {
    formik.resetForm()
    setIsModalOpen(false)
    closeModal()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size='lg'
      className='create-form-modal'
      backdrop='static'
    >
      <Modal.Header closeButton className='border-0 pb-0'>
        <div className='d-flex align-items-center gap-3'>
          <div className='create-form-modal__header-icon'>
            <i className='bi bi-ui-checks' />
          </div>
          <div>
            <h2 className='create-form-modal__title'>Create New Form</h2>
            <p className='create-form-modal__subtitle'>
              Set up your form details before you start building
            </p>
          </div>
        </div>
      </Modal.Header>

      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='create-form-modal__section'>
            <span className='create-form-modal__section-label'>Basic details</span>

            <div className='create-form-modal__field'>
              <label className='create-form-modal__label' htmlFor='create-form-name'>
                <span>Form name</span>
                <span className='create-form-modal__char-count'>
                  {formik.values.formName.length}/50
                </span>
              </label>
              <input
                id='create-form-name'
                {...formik.getFieldProps('formName')}
                className={clsx('form-control create-form-modal__input', {
                  'is-invalid': formik.touched.formName && formik.errors.formName
                })}
                type='text'
                placeholder='e.g. Customer Feedback Survey'
                autoComplete='off'
                maxLength={50}
              />
              {formik.touched.formName && formik.errors.formName && (
                <div className='create-form-modal__error' role='alert'>
                  {formik.errors.formName}
                </div>
              )}
            </div>

            <div className='create-form-modal__field'>
              <label className='create-form-modal__label' htmlFor='create-form-description'>
                <span>Description</span>
                <span className='create-form-modal__char-count'>
                  {formik.values.formDescription.length}/100
                </span>
              </label>
              <textarea
                id='create-form-description'
                {...formik.getFieldProps('formDescription')}
                className={clsx('form-control create-form-modal__textarea', {
                  'is-invalid': formik.touched.formDescription && formik.errors.formDescription
                })}
                placeholder='Briefly describe what this form is for'
                autoComplete='off'
                maxLength={100}
              />
              {formik.touched.formDescription && formik.errors.formDescription && (
                <div className='create-form-modal__error' role='alert'>
                  {formik.errors.formDescription}
                </div>
              )}
            </div>
          </div>

          <div className='create-form-modal__section'>
            <span className='create-form-modal__section-label'>Form type</span>
            <div className='create-form-modal__type-grid'>
              {FORM_TYPES.map((type) => {
                const isSelected = formik.values.formType === type.value
                return (
                  <button
                    key={type.value}
                    type='button'
                    className={clsx('create-form-modal__type-card', {
                      'create-form-modal__type-card--selected': isSelected
                    })}
                    onClick={() => formik.setFieldValue('formType', type.value)}
                  >
                    <span className='create-form-modal__type-check'>
                      <i className='bi bi-check' />
                    </span>
                    <div className='create-form-modal__type-card-top'>
                      <span className={clsx('create-form-modal__type-icon', type.iconClass)}>
                        <i className={`bi ${type.icon}`} />
                      </span>
                      <span className='create-form-modal__type-name'>{type.label}</span>
                    </div>
                    <p className='create-form-modal__type-desc'>{type.description}</p>
                  </button>
                )
              })}
            </div>
          </div>

          <div className='create-form-modal__section mb-0'>
            <span className='create-form-modal__section-label'>Settings</span>
            <div className='create-form-modal__field'>
              <label className='create-form-modal__label' htmlFor='create-form-expiry'>
                <span>Expiry date</span>
                <span className='create-form-modal__optional'>Optional</span>
              </label>
              <div className='create-form-modal__expiry-wrap'>
                <i className='bi bi-calendar3 create-form-modal__expiry-icon' />
                <input
                  id='create-form-expiry'
                  {...formik.getFieldProps('expiryDate')}
                  className={clsx(
                    'form-control create-form-modal__input create-form-modal__input--date',
                    {
                      'is-invalid': formik.touched.expiryDate && formik.errors.expiryDate
                    }
                  )}
                  type='date'
                  autoComplete='off'
                />
              </div>
              <p className='create-form-modal__hint'>
                Leave blank if the form should stay open indefinitely.
              </p>
              {formik.touched.expiryDate && formik.errors.expiryDate && (
                <div className='create-form-modal__error' role='alert'>
                  {formik.errors.expiryDate}
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type='button' className='btn create-form-modal__btn-cancel' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn-primary create-form-modal__btn-submit'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formik.isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
                Creating...
              </>
            ) : (
              <>
                <i className='bi bi-plus-lg me-2' />
                Create Form
              </>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default CreateFormModal
